import { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  makeStyles,
} from '@material-ui/core';
import AutoAwesomeIcon from '@material-ui/icons/EmojiObjects';
import HistoryIcon from '@material-ui/icons/History';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import Alert from '@material-ui/lab/Alert';
import {
  Env,
  ServiceEntry,
  ReleaseNoteRecord,
  aggregateAndClean,
} from '../promoteUtils';
import { VersionMap } from './StepSelect';

const useStyles = makeStyles((theme) => ({
  choiceCard: {
    padding: theme.spacing(3),
    textAlign: 'left',
    cursor: 'pointer',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 6,
    height: '100%',
    transition: 'border-color 0.15s',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  choiceCardActive: {
    borderColor: theme.palette.primary.main,
    background:
      theme.palette.type === 'dark'
        ? 'rgba(139, 92, 246, 0.08)'
        : 'rgba(139, 92, 246, 0.04)',
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  serviceName: {
    fontFamily: 'monospace',
    fontWeight: 600,
    fontSize: 13,
  },
  versionRange: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
}));

export type NotesMode = 'generate' | 'existing' | 'skip' | null;
export type NotesPhase = 'idle' | 'fetching' | 'done' | 'error';

export interface PerServiceFetch {
  service: string;
  status: 'pending' | 'fetching' | 'done' | 'error';
  count?: number;
  error?: string;
}

interface GqlResponse<T> {
  data?: T;
  errors?: { message: string }[];
}

export interface StepNotesProps {
  selectedServices: ServiceEntry[];
  versions: VersionMap;
  sourceEnv: Env;
  targetEnv: Env;
  notesMode: NotesMode;
  setNotesMode: (v: NotesMode) => void;
  phase: NotesPhase;
  setPhase: (p: NotesPhase) => void;
  perService: PerServiceFetch[];
  setPerService: (next: PerServiceFetch[]) => void;
  markdown: string;
  setMarkdown: (md: string) => void;
  bundleVersion: string;
  setBundleVersion: (v: string) => void;
}

export const StepNotes = ({
  selectedServices,
  versions,
  sourceEnv,
  targetEnv,
  notesMode,
  setNotesMode,
  phase,
  setPhase,
  perService,
  setPerService,
  markdown,
  setMarkdown,
  bundleVersion,
  setBundleVersion,
}: StepNotesProps) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);

  // AI-generate branch
  useEffect(() => {
    if (notesMode !== 'generate' || phase !== 'idle') return;

    const backendUrl = config.getString('backend.baseUrl');
    const proxyBase = `${backendUrl}/api/proxy/shrek/service-release-notes`;

    setPhase('fetching');
    const initial: PerServiceFetch[] = selectedServices.map((s) => ({
      service: s.name,
      status: 'pending',
    }));
    setPerService(initial);

    let cancelled = false;

    (async () => {
      const results: { service: string; notes: ReleaseNoteRecord[] }[] = [];
      const next = [...initial];

      await Promise.all(
        selectedServices.map(async (svc, i) => {
          next[i] = { ...next[i], status: 'fetching' };
          setPerService([...next]);

          const sourceV = versions[svc.name]?.[sourceEnv];
          const targetV = versions[svc.name]?.[targetEnv];

          if (!sourceV || !targetV) {
            next[i] = { ...next[i], status: 'error', error: 'missing version' };
            setPerService([...next]);
            return;
          }

          try {
            // Shrek range: excludes `from`, includes `to`
            const url = `${proxyBase}/${encodeURIComponent(
              svc.name,
            )}/range?from=${encodeURIComponent(targetV)}&to=${encodeURIComponent(sourceV)}`;
            const res = await backstageFetch(url);
            if (!res.ok) throw new Error(`${res.status}`);
            const notes = (await res.json()) as ReleaseNoteRecord[];
            results.push({ service: svc.name, notes });
            next[i] = { ...next[i], status: 'done', count: notes.length };
          } catch (err: any) {
            next[i] = { ...next[i], status: 'error', error: err.message || 'fetch failed' };
          }
          setPerService([...next]);
        }),
      );

      if (cancelled) return;
      const md = await aggregateAndClean(results, backstageFetch, backendUrl);
      setMarkdown(md);
      setPhase('done');
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notesMode]);

  if (notesMode === null) {
    return <ModePicker setNotesMode={setNotesMode} classes={classes} count={selectedServices.length} />;
  }

  if (notesMode === 'skip') {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200} flexDirection="column" gridGap={8}>
        <Typography variant="body2" color="textSecondary">
          Skipping release notes.
        </Typography>
        <Button size="small" onClick={() => setNotesMode(null)}>
          Change mind
        </Button>
      </Box>
    );
  }

  if (notesMode === 'existing') {
    return (
      <ExistingBundlePicker
        phase={phase}
        setPhase={setPhase}
        markdown={markdown}
        setMarkdown={setMarkdown}
        bundleVersion={bundleVersion}
        setBundleVersion={setBundleVersion}
        onChangeMind={() => setNotesMode(null)}
      />
    );
  }

  // notesMode === 'generate'
  return (
    <Paper variant="outlined" style={{ padding: 16 }}>
      <Box display="flex" alignItems="center" gridGap={12} mb={2}>
        <AutoAwesomeIcon color="primary" />
        <Box flex={1}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            {phase === 'done' ? 'Notes aggregated' : 'Fetching per-service notes…'}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {phase === 'done'
              ? 'Combined into a single set. Review and edit on the next step.'
              : 'Calling service-release-notes · one request per service'}
          </Typography>
        </Box>
        {phase === 'fetching' && <CircularProgress size={18} />}
        {phase === 'done' && markdown && (
          <Chip size="small" label="ready" color="primary" />
        )}
      </Box>

      {perService.some((p) => p.status === 'error') && (
        <Box mb={2}>
          <Alert severity="warning">
            Some services failed to fetch — aggregated notes may be incomplete.
          </Alert>
        </Box>
      )}

      <Grid container spacing={1}>
        {perService.map((p) => {
          const svc = selectedServices.find((s) => s.name === p.service);
          const targetV = svc && versions[svc.name]?.[targetEnv];
          const sourceV = svc && versions[svc.name]?.[sourceEnv];
          return (
            <Grid item xs={12} md={6} key={p.service}>
              <div className={classes.progressRow}>
                <div style={{ flex: 1 }}>
                  <div className={classes.serviceName}>{p.service}</div>
                  <div className={classes.versionRange}>
                    {targetV ?? '—'}{' '}
                    <ArrowForwardIcon style={{ fontSize: 12, verticalAlign: 'middle' }} />{' '}
                    {sourceV ?? '—'}
                  </div>
                </div>
                {p.status === 'fetching' && <CircularProgress size={14} />}
                {p.status === 'done' && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${p.count ?? 0} ${p.count === 1 ? 'note' : 'notes'}`}
                  />
                )}
                {p.status === 'error' && (
                  <Chip size="small" label={p.error ?? 'error'} color="secondary" />
                )}
                {p.status === 'pending' && (
                  <Chip size="small" variant="outlined" label="queued" />
                )}
              </div>
            </Grid>
          );
        })}
      </Grid>
    </Paper>
  );
};

function ModePicker({
  setNotesMode,
  classes,
  count,
}: {
  setNotesMode: (m: NotesMode) => void;
  classes: ReturnType<typeof useStyles>;
  count: number;
}) {
  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight={360}>
      <Paper variant="outlined" style={{ padding: 32, maxWidth: 820, width: '100%' }}>
        <Box display="flex" alignItems="center" gridGap={8} mb={1}>
          <AutoAwesomeIcon color="primary" />
          <Typography variant="h6">Release notes for this promotion</Typography>
        </Box>
        <Typography variant="body2" color="textSecondary" paragraph>
          Aggregate AI-generated notes from the service-release-notes service, pick an
          existing published bundle, or skip notes entirely.
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              className={classes.choiceCard}
              onClick={() => setNotesMode('generate')}
            >
              <Box display="flex" alignItems="center" gridGap={8} mb={1}>
                <AutoAwesomeIcon color="primary" />
                <Typography variant="subtitle2">Generate new</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Aggregate from {count} services, then edit & publish.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              className={classes.choiceCard}
              onClick={() => setNotesMode('existing')}
            >
              <Box display="flex" alignItems="center" gridGap={8} mb={1}>
                <HistoryIcon color="primary" />
                <Typography variant="subtitle2">Use existing bundle</Typography>
              </Box>
              <Typography variant="body2" color="textSecondary">
                Pick a previously published release bundle. No editing.
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper
              variant="outlined"
              className={classes.choiceCard}
              onClick={() => setNotesMode('skip')}
            >
              <Typography variant="subtitle2">Skip notes</Typography>
              <Typography variant="body2" color="textSecondary">
                Just promote the services.
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

function ExistingBundlePicker({
  phase,
  setPhase,
  markdown,
  setMarkdown,
  bundleVersion,
  setBundleVersion,
  onChangeMind,
}: {
  phase: NotesPhase;
  setPhase: (p: NotesPhase) => void;
  markdown: string;
  setMarkdown: (md: string) => void;
  bundleVersion: string;
  setBundleVersion: (v: string) => void;
  onChangeMind: () => void;
}) {
  const config = useApi(configApiRef);
  const { fetch: backstageFetch } = useApi(fetchApiRef);
  const graphqlUrl = `${config.getString('backend.baseUrl')}/api/proxy/release-notes/graphql`;

  const [versions, setVersions] = useState<string[]>([]);
  const [currentVersion, setCurrentVersion] = useState<string | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingChangelog, setLoadingChangelog] = useState(false);

  const gql = useCallback(
    async <T,>(query: string, variables?: Record<string, unknown>): Promise<GqlResponse<T>> => {
      const res = await backstageFetch(graphqlUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables }),
      });
      return res.json();
    },
    [backstageFetch, graphqlUrl],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [vs, cur] = await Promise.all([
          gql<{ releaseVersions: string[] }>(`query { releaseVersions }`),
          gql<{ currentRelease: { version: string } | null }>(
            `query { currentRelease { version } }`,
          ),
        ]);
        if (cancelled) return;
        setVersions(vs.data?.releaseVersions ?? []);
        setCurrentVersion(cur.data?.currentRelease?.version ?? null);
      } catch (err: any) {
        if (!cancelled) setLoadError(err.message ?? 'failed to load versions');
      } finally {
        if (!cancelled) setLoadingVersions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gql]);

  const loadChangelog = useCallback(
    async (version: string) => {
      setLoadingChangelog(true);
      setPhase('fetching');
      try {
        const result = await gql<{ release: { version: string; changelog: string } | null }>(
          `query GetRelease($version: String!) {
            release(version: $version) { version changelog }
          }`,
          { version },
        );
        if (result.errors) throw new Error(result.errors[0].message);
        const rel = result.data?.release;
        if (!rel) throw new Error('release not found');
        setMarkdown(rel.changelog);
        setBundleVersion(rel.version);
        setPhase('done');
      } catch (err: any) {
        setLoadError(err.message ?? 'failed to load changelog');
        setPhase('error');
      } finally {
        setLoadingChangelog(false);
      }
    },
    [gql, setBundleVersion, setMarkdown, setPhase],
  );

  return (
    <Paper variant="outlined" style={{ padding: 24 }}>
      <Box display="flex" alignItems="center" gridGap={12} mb={2}>
        <HistoryIcon color="primary" />
        <Box flex={1}>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Select an existing release bundle
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Loaded from release-notes · pick one to use for this promotion
          </Typography>
        </Box>
        <Button size="small" onClick={onChangeMind}>
          Change mind
        </Button>
      </Box>

      {loadError && (
        <Box mb={2}>
          <Alert severity="error">{loadError}</Alert>
        </Box>
      )}

      {loadingVersions ? (
        <Box display="flex" alignItems="center" gridGap={8}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="textSecondary">
            Loading bundles…
          </Typography>
        </Box>
      ) : versions.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          No published release bundles found. Generate new, or skip notes.
        </Typography>
      ) : (
        <>
          <FormControl variant="outlined" size="small" style={{ minWidth: 260 }}>
            <InputLabel id="bundle-version-label">Bundle version</InputLabel>
            <Select
              labelId="bundle-version-label"
              value={bundleVersion}
              onChange={(e) => loadChangelog(String(e.target.value))}
              label="Bundle version"
              disabled={loadingChangelog}
            >
              {versions.map((v) => (
                <MenuItem key={v} value={v}>
                  {v}
                  {v === currentVersion ? ' (current)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {loadingChangelog && (
            <Box display="flex" alignItems="center" gridGap={8} mt={2}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="textSecondary">
                Loading changelog…
              </Typography>
            </Box>
          )}

          {phase === 'done' && markdown && (
            <Box mt={2}>
              <Alert severity="success">
                Loaded bundle <strong>{bundleVersion}</strong> · {markdown.split('\n').length} lines.
                Proceed to Confirm.
              </Alert>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}
